import { useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { FaCheck } from "react-icons/fa";
import { toast } from "react-toastify";
import styles from "../../../styles/FormPreview.module.css";
import FormStepRenderer from "./FormStepRenderer";
import OfferDisplay from "./OfferDisplay";
import PhoneNumberInput from "./PhoneNumberInput";

const hexToRgb = (hex) => {
    if (!hex) return null;
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
    ] : null;
};

const FormPreview = ({
    steps,
    formData: externalFormData,
    onChange,
    inventoryItems,
    inventoryLoading = false,
    onSubmit,
    formMode = "published",
    theme = {},
    showSteps = false,
    stepIndicatorStyle = "progress",
    formStyle = "typeform",
    onStepUpdate,
    submitting = false,
    submitButtonText = "Submit Form",
    submitDisabled = false,
    useStepIndexKey = false,
}) => {
    // Use internal state for formData if onChange is not provided (preview mode)
    const [internalFormData, setInternalFormData] = useState(externalFormData || {});
    const formData = onChange ? externalFormData : internalFormData;

    // When true (document form), file/image steps use a unique key per step so each has its own formData slot.
    // Prefer step._stepKey (set by DocumentFormViewer) so keys stay correct regardless of flatMap/map indices.
    const getStepKey = (step, index) => {
        if (step && step._stepKey) return step._stepKey;
        if (useStepIndexKey && step && (step.type === "file" || step.type === "image")) {
            return `${step.label}__${index}`;
        }
        return step ? step.label : "";
    };

    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [inputComplete, setInputComplete] = useState(false);
    const [editingHeader, setEditingHeader] = useState(false);
    const [editingInstructions, setEditingInstructions] = useState(false);
    const [headerValue, setHeaderValue] = useState("");
    const [instructionsValue, setInstructionsValue] = useState("");
    const [editingSinglePageHeader, setEditingSinglePageHeader] = useState({});
    const [editingSinglePageInstructions, setEditingSinglePageInstructions] = useState({});
    const [singlePageHeaderValues, setSinglePageHeaderValues] = useState({});
    const [singlePageInstructionsValues, setSinglePageInstructionsValues] = useState({});
    const [emailValidationErrors, setEmailValidationErrors] = useState({});
    
    // Sync external formData when it changes
    useEffect(() => {
        if (onChange) {
            // If onChange is provided, use external formData directly
            // Don't manage internal state
        } else {
            // If no onChange, use internal state and sync with external formData when it changes
            if (externalFormData !== undefined) {
                setInternalFormData(externalFormData);
            }
        }
    }, [externalFormData, onChange]);

    const currentStep = steps[currentStepIndex];
    const isLastStep = currentStepIndex === steps.length - 1;
    const inputRef = useRef();
    const headerInputRef = useRef();
    const instructionsInputRef = useRef();
    const isEditMode = formMode === "draft" && onStepUpdate;

    // Update local state when step changes
    useEffect(() => {
        if (currentStep) {
            setHeaderValue(currentStep.header || "");
            setInstructionsValue(currentStep.instructions || "");
        }
    }, [currentStepIndex, currentStep]);

    // Initialize single page header and instructions values
    useEffect(() => {
        if (formStyle === "singlePage" && steps.length > 0) {
            const headerVals = {};
            const instructionVals = {};
            steps.forEach((step, index) => {
                headerVals[index] = step.header || "";
                instructionVals[index] = step.instructions || "";
            });
            setSinglePageHeaderValues(headerVals);
            setSinglePageInstructionsValues(instructionVals);
        }
    }, [formStyle, steps]);

    // Helper: for file/image steps, complete only when at least one File is present
    const hasValidStepValue = (step, value) => {
        if (step.type === "file" || step.type === "image") {
            if (value == null) return false;
            const list = Array.isArray(value) ? value : [value];
            return list.some(f => f instanceof File);
        }
        return value !== null && value !== undefined && value !== "";
    };

    // Check if current step has a value and is valid when step changes
    useEffect(() => {
        if (currentStep && currentStep.type !== "offer") {
            const stepKey = useStepIndexKey && (currentStep.type === "file" || currentStep.type === "image")
                ? `${currentStep.label}__${currentStepIndex}`
                : currentStep.label;
            const stepValue = formData[stepKey];

            // For select type, they auto-advance, so inputComplete is not needed
            if (currentStep.type === "select") {
                setInputComplete(false);
                return;
            }

            const hasValue = hasValidStepValue(currentStep, stepValue);

            // For input fields, also check validation
            if (hasValue && inputRef.current && ["text", "email", "tel"].includes(currentStep.type)) {
                // Use setTimeout to ensure the DOM has updated
                // For phone numbers, give a bit more time for async validation
                const delay = currentStep.type === "tel" ? 100 : 0;
                setTimeout(() => {
                    const isValid = inputRef.current?.checkValidity() ?? false;
                    setInputComplete(isValid);
                }, delay);
            } else {
                // For selection-based steps (cards, options, etc.), only set complete if value exists
                // For file/image: only complete when at least one file is added
                setInputComplete(hasValue);
            }
        } else {
            setInputComplete(false);
        }
    }, [currentStepIndex, currentStep, formData, useStepIndexKey]);


    const appliedTheme = {
        backgroundColor: theme?.backgroundColor || "#ffffff",
        headingColor: theme?.headingColor || "#181818",
        instructionsColor: theme?.instructionsColor || "#464646",
        cardBackgroundColor: theme?.cardBackgroundColor || "#d9d9d9",
        cardTextColor: theme?.cardTextColor || "#3f3f3f",
        cardBorderColor: theme?.cardBorderColor || "#e5e7eb",
        cardHoverBorderColor: theme?.cardHoverBorderColor || "#348C9E",
        buttonColor: theme?.buttonColor || "#007bff",
        buttonTextColor: theme?.buttonTextColor || "#ffffff",
        buttonHoverColor: theme?.buttonHoverColor || "#0056b3",
        fontFamily: theme?.fontFamily || "Arial, sans-serif",
        headerFontSize: theme?.headerFontSize || "1.8rem",
        headerFontWeight: theme?.headerFontWeight || "600",
        instructionsFontSize: theme?.instructionsFontSize || "1rem",
        inputBackground: theme?.inputBackground || "#ffffff",
        inputBorderColor: theme?.inputBorderColor || "#e5e7eb",
        inputFocusBorderColor: theme?.inputFocusBorderColor || "#348C9E",
        inputHoverBorderColor: theme?.inputHoverBorderColor || "#d1d5db",
        inputTextColor: theme?.inputTextColor || "#333333",
        progressBarColor: theme?.progressBarColor || "#348C9E",
        progressBarBackground: theme?.progressBarBackground || "rgba(0, 0, 0, 0.06)",
        progressIndicatorActiveColor: theme?.progressIndicatorActiveColor || theme?.buttonColor || "#348C9E",
        progressIndicatorInactiveColor: theme?.progressIndicatorInactiveColor || "#e9ecef",
        progressIndicatorTextColor: theme?.progressIndicatorTextColor || "#ffffff",
        progressIndicatorInactiveTextColor: theme?.progressIndicatorInactiveTextColor || "#6c757d",
        progressIndicatorConnectorColor: theme?.progressIndicatorConnectorColor || "#e9ecef",
        formBorderRadius: theme?.formBorderRadius || "24px",
        cardBorderRadius: theme?.cardBorderRadius || "16px",
        inputBorderRadius: theme?.inputBorderRadius || "12px",
        buttonBorderRadius: theme?.buttonBorderRadius || "12px",
        cardShadowIntensity: theme?.cardShadowIntensity || "0.08",
        inputShadowIntensity: theme?.inputShadowIntensity || "0.05",
        buttonShadowIntensity: theme?.buttonShadowIntensity || "0.1",
        wrapperOpacity: theme?.wrapperOpacity ?? 1,
    };

    // Email validation function
    const validateEmail = (email) => {
        if (!email || email.trim() === "") return null; // Empty is valid (unless required)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email.trim());
    };

    const handleStepChange = (key, value) => {
        // Find all steps that depend on this field (recursively).
        // This prevents stale values when there are deeper dependency chains.
        const fieldsToClearSet = new Set();
        const queue = [key];
        while (queue.length) {
            const parent = queue.shift();
            const directChildren = steps
                .filter((step) => step.dependsOn === parent)
                .map((step) => step.label);
            directChildren.forEach((child) => {
                if (!fieldsToClearSet.has(child)) {
                    fieldsToClearSet.add(child);
                    queue.push(child);
                }
            });
        }
        // Do not clear the field being set
        fieldsToClearSet.delete(key);
        const allFieldsToClear = Array.from(fieldsToClearSet);
        
        // Clear dependent fields when parent field changes
        const updates = { [key]: value };
        allFieldsToClear.forEach(field => {
            updates[field] = null; // Clear dependent fields
        });

        // If any field that impacts the offer changes, clear the stored offer price too.
        // "Offer Price" is not a step in most forms, so it won't be cleared by dependsOn logic.
        const offerDrivers = new Set(["Category", "Product Name", "Primary Variant", "Condition"]);
        const offerIsInvalidated =
            offerDrivers.has(key) || allFieldsToClear.some((f) => offerDrivers.has(f));
        if (offerIsInvalidated) {
            updates["Offer Price"] = null;
        }
        
        // Update internal state if onChange is not provided or is empty
        if (!onChange || typeof onChange !== 'function') {
            setInternalFormData(prev => ({ ...prev, ...updates }));
        } else {
            // Call onChange for each field that needs to be updated
            Object.entries(updates).forEach(([fieldKey, fieldValue]) => {
                onChange(fieldKey, fieldValue);
            });
        }

        // Validate email fields
        if (formStyle === "singlePage") {
            const step = steps.find(s => s.label === key);
            if (step?.type === "email") {
                const isValid = validateEmail(value);
                if (value && value.trim() !== "") {
                    setEmailValidationErrors(prev => ({
                        ...prev,
                        [key]: isValid ? null : "Please enter a valid email address"
                    }));
                } else {
                    setEmailValidationErrors(prev => ({
                        ...prev,
                        [key]: null
                    }));
                }
            }
        }

        if (currentStep.type === "select") {
            setTimeout(() => {
                if (!isLastStep) {
                    setCurrentStepIndex(prev => prev + 1);
                }
            }, 300);
        } else {
            // Check if the value is not empty (for file/image: at least one File)
            const hasValue = hasValidStepValue(currentStep, value);

            // For input fields, also check validation
            if (hasValue && ["text", "email", "tel"].includes(currentStep.type)) {
                // Use setTimeout to ensure the DOM has updated with the new value
                // For phone numbers, give a bit more time for async validation
                const delay = currentStep.type === "tel" ? 100 : 0;
                setTimeout(() => {
                    const isValid = inputRef.current?.checkValidity() ?? false;
                    setInputComplete(isValid);
                }, delay);
            } else {
                setInputComplete(hasValue);
            }
        }
    };

    const goToNextStep = () => {
        const inputEl = inputRef.current;
        if (inputEl && !inputEl.checkValidity()) {
            inputEl.reportValidity();
            return;
        }

        if (!isLastStep) {
            setCurrentStepIndex(prev => prev + 1);
            setInputComplete(false);
        }
    };

    const goToPreviousStep = () => {
        setCurrentStepIndex(prev => Math.max(prev - 1, 0));
        // inputComplete will be set by useEffect based on whether the step has a value
    };

    const restartForm = () => {
        setCurrentStepIndex(0);
        setInputComplete(false);
    };

    if (!steps || steps.length === 0) {
        return (
            <div className={styles.emptyState}>
                <p>No fields added yet. Use the form builder to add steps.</p>
            </div>
        );
    }

    // Single Page Form View
    if (formStyle === "singlePage") {
        return (
            <div
                className={`${styles.singlePageContainer} ${formMode === "draft" ? styles.draftMode : styles.productionMode}`}
                style={{
                    backgroundImage: theme?.backgroundImageUrl ? `url(${theme.backgroundImageUrl})` : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    fontFamily: appliedTheme.fontFamily,
                    backgroundColor: appliedTheme.backgroundColor || "#f5f7fa",
                    minHeight: "100vh",
                }}
            >
                {formMode === "draft" && (
                    <div
                        className={styles.draftBanner}
                        style={{
                            backgroundColor: appliedTheme.bannerBackground,
                        }}
                    >
                        🚧 This form is in <strong>draft mode</strong>. Submissions are disabled.
                    </div>
                )}

                <div className={styles.singlePageWrapper}>
                    <div className={styles.singlePageFormContainer}>
                        {theme?.logoUrl && (
                            <div className={styles.singlePageLogo}>
                                <img src={theme.logoUrl} alt="Form Logo" className={styles.singlePageLogoImage} />
                            </div>
                        )}

                        <div className={styles.singlePageForm}>
                                    {steps
                                        .flatMap((step, index) => {
                                            const isVisible = !step.dependsOn || formData[step.dependsOn];

                                            if (!isVisible) return [];

                                            // If this is an offer step, inject variant and condition dropdowns before it
                                            if (step.type === "offer") {
                                                const selectedProduct = formData["Product Name"];
                                                const matchedDevice = inventoryItems.find(item =>
                                                    item.productName?.toLowerCase().trim() === selectedProduct?.toLowerCase().trim()
                                                );
                                                
                                                const hasVariantStep = steps.some(s => s.label === "Primary Variant" && s.type === "select");
                                                const hasConditionStep = steps.some(s => s.label === "Condition" && s.type === "select");
                                                
                                                const variants = matchedDevice?.items?.map((v) => v.primary).filter(Boolean) ?? [];
                                                const selectedVariant = formData["Primary Variant"] || variants[0] || "";
                                                const variantData = matchedDevice?.items?.find(v => v.primary === selectedVariant);
                                                const PricingTiers = variantData?.prices?.reduce((acc, tier) => {
                                                    acc[tier.name] = tier.price;
                                                    return acc;
                                                }, {}) ?? {};
                                                const availableConditions = Object.entries(PricingTiers).filter(([name]) => name !== "Selling");
                                                
                                                const injectedSteps = [];
                                                
                                                // Inject variant dropdown if it doesn't exist and we have variants
                                                if (!hasVariantStep && variants.length > 0 && matchedDevice) {
                                                    injectedSteps.push({
                                                        type: "select",
                                                        label: "Primary Variant",
                                                        header: "Storage Capacity",
                                                        instructions: "Select the storage capacity",
                                                        options: variants.map(v => ({ label: v, parent: selectedProduct })),
                                                        isInjected: true
                                                    });
                                                }
                                                
                                                // Inject condition dropdown if it doesn't exist (always inject it, it will show options when variant is selected)
                                                if (!hasConditionStep && matchedDevice) {
                                                    injectedSteps.push({
                                                        type: "select",
                                                        label: "Condition",
                                                        header: "Device Condition",
                                                        instructions: "Select the condition of your device",
                                                        dependsOn: "Primary Variant",
                                                        options: availableConditions.map(([name]) => ({ label: name, parent: selectedVariant })),
                                                        isInjected: true
                                                    });
                                                }
                                                
                                                return [...injectedSteps, step];
                                            }
                                            
                                            return [step];
                                        })
                                        .map((step, index) => {
                                            const stepKey = getStepKey(step, index);
                                            const stepValue = formData[stepKey];
                                            const isVisible = !step.dependsOn || formData[step.dependsOn];

                                            if (!isVisible) return null;

                                            // Get filtered options for select fields
                                            const getFilteredOptions = () => {
                                                const allOptions = step.options || [];
                                                
                                                // If this is an injected variant step
                                                if (step.isInjected && step.label === "Primary Variant") {
                                                    const selectedProduct = formData["Product Name"];
                                                    const matchedDevice = inventoryItems.find(item =>
                                                        item.productName?.toLowerCase().trim() === selectedProduct?.toLowerCase().trim()
                                                    );
                                                    const variants = matchedDevice?.items?.map((v) => v.primary).filter(Boolean) ?? [];
                                                    return variants.map(v => ({ label: v, parent: selectedProduct }));
                                                }
                                                
                                                // If this is an injected condition step
                                                if (step.isInjected && step.label === "Condition") {
                                                    const selectedProduct = formData["Product Name"];
                                                    const selectedVariant = formData["Primary Variant"];
                                                    
                                                    // If no variant is selected, return empty array
                                                    if (!selectedVariant) {
                                                        return [];
                                                    }
                                                    
                                                    const matchedDevice = inventoryItems.find(item =>
                                                        item.productName?.toLowerCase().trim() === selectedProduct?.toLowerCase().trim()
                                                    );
                                                    const variantData = matchedDevice?.items?.find(v => v.primary === selectedVariant);
                                                    const PricingTiers = variantData?.prices?.reduce((acc, tier) => {
                                                        acc[tier.name] = tier.price;
                                                        return acc;
                                                    }, {}) ?? {};
                                                    const availableConditions = Object.entries(PricingTiers)
                                                        .filter(([name]) => name !== "Selling")
                                                        .map(([name]) => ({ label: name, parent: selectedVariant }));
                                                    return availableConditions;
                                                }
                                                
                                                // If this is a "Category" step, generate options from inventory categories
                                                if (step.label === "Category" && inventoryItems.length > 0) {
                                                    const categories = inventoryItems
                                                        .map(item => item.category)
                                                        .filter(category => category && category.trim())
                                                        .filter((category, idx, array) => array.indexOf(category) === idx)
                                                        .sort();
                                                    
                                                    return categories.map(category => ({
                                                        label: category,
                                                        parent: null
                                                    }));
                                                }
                                                
                                                // If this is a "Product Name" step and depends on "Category"
                                                if (step.label === "Product Name" && step.dependsOn === "Category" && formData) {
                                                    const selectedCategory = formData[step.dependsOn];
                                                    
                                                    if (selectedCategory && inventoryItems.length > 0) {
                                                        const categoryProducts = inventoryItems
                                                            .filter(item => {
                                                                const matches = item.category?.toLowerCase() === selectedCategory.toLowerCase();
                                                                return matches;
                                                            })
                                                            .map(item => ({
                                                                label: item.productName,
                                                                parent: selectedCategory,
                                                                imageUrl: item.imageUrl || null
                                                            }))
                                                            .filter((item, idx, array) => 
                                                                array.findIndex(i => i.label === item.label) === idx
                                                            );
                                                        
                                                        return categoryProducts;
                                                    }
                                                    return [];
                                                }
                                                
                                                // If this is a "Primary Variant" step and depends on "Product Name"
                                                if (step.label === "Primary Variant" && step.dependsOn === "Product Name" && formData) {
                                                    const selectedProduct = formData[step.dependsOn];
                                                    
                                                    if (selectedProduct && inventoryItems.length > 0) {
                                                        const matchedItem = inventoryItems.find(item =>
                                                            item.productName?.toLowerCase() === selectedProduct.toLowerCase()
                                                        );
                                                        
                                                        if (matchedItem && Array.isArray(matchedItem.items)) {
                                                            const variantsWithImages = matchedItem.items
                                                                .filter(item => item.primary)
                                                                .map(item => {
                                                                    const matchingVariant = matchedItem.items.find(i => 
                                                                        i.primary === item.primary
                                                                    );
                                                                    return {
                                                                        label: item.primary,
                                                                        parent: selectedProduct,
                                                                        imageUrl: matchingVariant?.imageUrl || matchedItem?.imageUrl || null
                                                                    };
                                                                });
                                                            
                                                            return variantsWithImages;
                                                        }
                                                    }
                                                    return [];
                                                }
                                                
                                                // Default filtering logic for other cases
                                                if (!step.dependsOn || !formData) return allOptions;
                                                const parentValue = formData[step.dependsOn];
                                                if (!parentValue) return [];
                                                return allOptions.filter(opt =>
                                                    typeof opt === "object" ? opt.parent === parentValue : false
                                                );
                                            };

                                            const filteredOptions = getFilteredOptions();
                                            const selectedProductForOptions = formData["Product Name"];
                                            const productDetailsPending = step.label === "Primary Variant"
                                                && selectedProductForOptions
                                                && inventoryItems.some(item =>
                                                    item.productName?.toLowerCase().trim() === String(selectedProductForOptions).toLowerCase().trim()
                                                    && !Array.isArray(item.items)
                                                );
                                            const optionsPending = productDetailsPending
                                                || (inventoryLoading && (step.label === "Category" || step.label === "Product Name"));
                                            const dynamicLabel =
                                                step.dynamicLabelFrom && step.dependsOn && formData?.[step.dependsOn]
                                                    ? step.dynamicLabelFrom[formData[step.dependsOn]] || step.label
                                                    : step.label;

                                            const isEditingHeader = editingSinglePageHeader[index];
                                            const isEditingInstructions = editingSinglePageInstructions[index];
                                            const currentHeaderValue = singlePageHeaderValues[index] ?? (step.header || "");
                                            const currentInstructionsValue = singlePageInstructionsValues[index] ?? (step.instructions || "");

                                            return (
                                                <div key={index} className={styles.singlePageFieldGroup}>
                                                    {/* Hide header/instructions for offer steps */}
                                                    {step.type !== "offer" && (
                                                        <>
                                                            {isEditMode && isEditingHeader ? (
                                                                <input
                                                                    type="text"
                                                                    value={currentHeaderValue}
                                                                    onChange={(e) => {
                                                                        setSinglePageHeaderValues(prev => ({ ...prev, [index]: e.target.value }));
                                                                    }}
                                                                    onBlur={() => {
                                                                        if (onStepUpdate) {
                                                                            onStepUpdate(index, { ...step, header: currentHeaderValue });
                                                                        }
                                                                        setEditingSinglePageHeader(prev => ({ ...prev, [index]: false }));
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter") {
                                                                            e.target.blur();
                                                                        } else if (e.key === "Escape") {
                                                                            setSinglePageHeaderValues(prev => ({ ...prev, [index]: step.header || "" }));
                                                                            setEditingSinglePageHeader(prev => ({ ...prev, [index]: false }));
                                                                        }
                                                                    }}
                                                                    className={styles.singlePageInlineEdit}
                                                                    style={{
                                                                        color: appliedTheme.headingColor,
                                                                        fontSize: appliedTheme.headerFontSize || "1.5rem",
                                                                        fontWeight: appliedTheme.headerFontWeight || "600",
                                                                        backgroundColor: "transparent",
                                                                        border: "2px dashed " + appliedTheme.buttonColor,
                                                                        borderRadius: "8px",
                                                                        padding: "0.5rem",
                                                                        width: "100%",
                                                                        outline: "none",
                                                                        marginBottom: "0.5rem",
                                                                    }}
                                                                    autoFocus
                                                                />
                                                            ) : (
                                                                <div className={styles.singlePageLabel}>
                                                                    <span 
                                                                        className={styles.singlePageLabelText}
                                                                        onClick={() => {
                                                                            if (isEditMode) {
                                                                                setEditingSinglePageHeader(prev => ({ ...prev, [index]: true }));
                                                                            }
                                                                        }}
                                                                        style={{ cursor: isEditMode ? "pointer" : "default" }}
                                                                    >
                                                                        {step.header || step.label || (isEditMode ? "Click to add header" : "")}
                                                                        {step.validation?.required && <span className={styles.required}> *</span>}
                                                                    </span>
                                                                    {isEditMode && isEditingInstructions ? (
                                                                        <textarea
                                                                            value={currentInstructionsValue}
                                                                            onChange={(e) => {
                                                                                setSinglePageInstructionsValues(prev => ({ ...prev, [index]: e.target.value }));
                                                                            }}
                                                                            onBlur={() => {
                                                                                if (onStepUpdate) {
                                                                                    onStepUpdate(index, { ...step, instructions: currentInstructionsValue });
                                                                                }
                                                                                setEditingSinglePageInstructions(prev => ({ ...prev, [index]: false }));
                                                                            }}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === "Escape") {
                                                                                    setSinglePageInstructionsValues(prev => ({ ...prev, [index]: step.instructions || "" }));
                                                                                    setEditingSinglePageInstructions(prev => ({ ...prev, [index]: false }));
                                                                                }
                                                                            }}
                                                                            className={styles.singlePageInlineEdit}
                                                                            style={{
                                                                                color: appliedTheme.instructionsColor,
                                                                                fontSize: appliedTheme.instructionsFontSize || "0.875rem",
                                                                                backgroundColor: "transparent",
                                                                                border: "2px dashed " + appliedTheme.buttonColor,
                                                                                borderRadius: "8px",
                                                                                padding: "0.5rem",
                                                                                width: "100%",
                                                                                outline: "none",
                                                                                minHeight: "3rem",
                                                                                resize: "vertical",
                                                                                marginTop: "0.25rem",
                                                                            }}
                                                                            autoFocus
                                                                        />
                                                                    ) : (
                                                                        (step.instructions || isEditMode) && (
                                                                            <span 
                                                                                className={styles.singlePageLabelHint}
                                                                                onClick={(e) => {
                                                                                    if (isEditMode) {
                                                                                        e.stopPropagation();
                                                                                        setEditingSinglePageInstructions(prev => ({ ...prev, [index]: true }));
                                                                                    }
                                                                                }}
                                                                                style={{ cursor: isEditMode ? "pointer" : "default" }}
                                                                            >
                                                                                {step.instructions || (isEditMode ? "Click to add instructions" : "")}
                                                                            </span>
                                                                        )
                                                                    )}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                    <div className={styles.singlePageFieldInput}>
                                                        {step.type === "offer" ? (
                                                            <div className={styles.singlePageOfferDisplay}>
                                                                {(() => {
                                                                    const selectedProduct = formData["Product Name"];
                                                                    const matchedDevice = inventoryItems.find(item =>
                                                                        item.productName?.toLowerCase().trim() === selectedProduct?.toLowerCase().trim()
                                                                    );
                                                                    
                                                                    if (!matchedDevice) {
                                                                        return (
                                                                            <div className={styles.singlePageOfferError}>
                                                                                <div className={styles.singlePageOfferErrorIcon}>📦</div>
                                                                                <p>Please select a product to see your offer.</p>
                                                                            </div>
                                                                        );
                                                                    }

                                                                    if (!Array.isArray(matchedDevice.items)) {
                                                                        return (
                                                                            <div className={styles.singlePageOfferError}>
                                                                                <p>Loading offer...</p>
                                                                            </div>
                                                                        );
                                                                    }

                                                                    const selectedVariant = formData["Primary Variant"] || "";
                                                                    const variantData = matchedDevice.items.find(v => v.primary === selectedVariant);
                                                                    const selectedCondition = formData["Condition"] || "";
                                                                    const PricingTiers = variantData?.prices?.reduce((acc, tier) => {
                                                                        acc[tier.name] = tier.price;
                                                                        return acc;
                                                                    }, {}) ?? {};
                                                                    const selectedPrice = PricingTiers[selectedCondition] ?? 0;

                                                                    // Only show offer if condition is selected
                                                                    if (!selectedCondition || selectedPrice === 0) {
                                                                        return null;
                                                                    }

                                                                    return (
                                                                        <div className={styles.singlePageOfferCard}>
                                                                            <div className={styles.singlePageOfferHeader}>
                                                                                <h3 className={styles.singlePageOfferTitle} style={{ color: appliedTheme.headingColor }}>
                                                                                    Instant Offer
                                                                                </h3>
                                                                            </div>
                                                                            <div className={styles.singlePageOfferContent}>
                                                                                <div className={styles.singlePageOfferProductInfo}>
                                                                                    {matchedDevice.imageUrl && (
                                                                                        <div className={styles.singlePageOfferImage}>
                                                                                            <img src={matchedDevice.imageUrl} alt={matchedDevice.productName} loading="lazy" decoding="async" />
                                                                                        </div>
                                                                                    )}
                                                                                    <div className={styles.singlePageOfferInfo}>
                                                                                        <div className={styles.singlePageOfferProductName} style={{ color: appliedTheme.headingColor }}>
                                                                                            {matchedDevice.productName}
                                                                                        </div>
                                                                                        {selectedVariant && (
                                                                                            <div className={styles.singlePageOfferVariant} style={{ color: appliedTheme.instructionsColor }}>
                                                                                                Storage: {selectedVariant}
                                                                                            </div>
                                                                                        )}
                                                                                        {selectedCondition && (
                                                                                            <div className={styles.singlePageOfferVariant} style={{ color: appliedTheme.instructionsColor }}>
                                                                                                Condition: {selectedCondition}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </div>

                                                                                {/* Price Display */}
                                                                                <div className={styles.singlePageOfferPriceSection}>
                                                                                    <div className={styles.singlePageOfferPrice} style={{ color: appliedTheme.buttonColor || "#348C9E" }}>
                                                                                        R{selectedPrice.toLocaleString()}
                                                                                    </div>
                                                                                    <div className={styles.singlePageOfferPriceNote} style={{ color: appliedTheme.instructionsColor }}>
                                                                                        Valid for 7 days
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        ) : step.type === "select" ? (
                                                            <Form.Select
                                                                value={stepValue || ""}
                                                                onChange={(e) => {
                                                                    handleStepChange(step.label, e.target.value);
                                                                    // If variant changes, reset condition
                                                                    if (step.label === "Primary Variant") {
                                                                        handleStepChange("Condition", null);
                                                                    }
                                                                    // If condition is selected, update offer price
                                                                    if (step.label === "Condition") {
                                                                        const selectedProduct = formData["Product Name"];
                                                                        const matchedDevice = inventoryItems.find(item =>
                                                                            item.productName?.toLowerCase().trim() === selectedProduct?.toLowerCase().trim()
                                                                        );
                                                                        const selectedVariant = formData["Primary Variant"];
                                                                        const variantData = matchedDevice?.items?.find(v => v.primary === selectedVariant);
                                                                        const PricingTiers = variantData?.prices?.reduce((acc, tier) => {
                                                                            acc[tier.name] = tier.price;
                                                                            return acc;
                                                                        }, {}) ?? {};
                                                                        const price = PricingTiers[e.target.value] ?? 0;
                                                                        if (price > 0) {
                                                                            handleStepChange("Offer Price", price);
                                                                        }
                                                                    }
                                                                }}
                                                                className={styles.singlePageSelect}
                                                                style={{
                                                                    borderRadius: appliedTheme.inputBorderRadius,
                                                                    borderColor: appliedTheme.inputBorderColor,
                                                                    backgroundColor: appliedTheme.inputBackground,
                                                                    color: appliedTheme.inputTextColor,
                                                                    boxShadow: `0 1px 3px rgba(0, 0, 0, ${appliedTheme.inputShadowIntensity || 0.05})`,
                                                                    padding: "0.75rem 1rem",
                                                                    fontSize: "1rem",
                                                                    lineHeight: "1.5",
                                                                    height: "auto",
                                                                    minHeight: "auto",
                                                                }}
                                                                onFocus={(e) => {
                                                                    e.target.style.borderColor = appliedTheme.inputFocusBorderColor || "#348C9E";
                                                                    e.target.style.boxShadow = `0 0 0 4px rgba(52, 140, 158, 0.1), 0 4px 12px rgba(52, 140, 158, 0.15)`;
                                                                }}
                                                                onBlur={(e) => {
                                                                    e.target.style.borderColor = appliedTheme.inputBorderColor || "#e5e7eb";
                                                                    e.target.style.boxShadow = `0 1px 3px rgba(0, 0, 0, ${appliedTheme.inputShadowIntensity || 0.05})`;
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (document.activeElement !== e.target) {
                                                                        e.target.style.borderColor = appliedTheme.inputHoverBorderColor || "#d1d5db";
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (document.activeElement !== e.target) {
                                                                        e.target.style.borderColor = appliedTheme.inputBorderColor || "#e5e7eb";
                                                                    }
                                                                }}
                                                            >
                                                                <option value="">{optionsPending ? "Loading..." : `Select ${dynamicLabel}...`}</option>
                                                                {filteredOptions.map((opt, i) => {
                                                                    const optionValue = typeof opt === "object" ? opt.label : opt;
                                                                    return (
                                                                        <option key={i} value={optionValue}>
                                                                            {optionValue}
                                                                        </option>
                                                                    );
                                                                })}
                                                            </Form.Select>
                                                        ) : step.type === "tel" ? (
                                                            <PhoneNumberInput
                                                                value={stepValue || ""}
                                                                onChange={(val) => handleStepChange(step.label, val)}
                                                                validation={step.validation}
                                                                theme={appliedTheme}
                                                                placeholder={dynamicLabel}
                                                                formStyle="singlePage"
                                                            />
                                                        ) : ["text", "email"].includes(step.type) ? (
                                                            <>
                                                                <Form.Control
                                                                    type={step.type}
                                                                    value={stepValue || ""}
                                                                    onChange={(e) => handleStepChange(step.label, e.target.value)}
                                                                    onBlur={(e) => {
                                                                        if (step.type === "email") {
                                                                            const isValid = validateEmail(e.target.value);
                                                                            if (e.target.value && e.target.value.trim() !== "") {
                                                                                setEmailValidationErrors(prev => ({
                                                                                    ...prev,
                                                                                    [step.label]: isValid ? null : "Please enter a valid email address"
                                                                                }));
                                                                            }
                                                                        }
                                                                        e.target.style.borderColor = emailValidationErrors[step.label] 
                                                                            ? "#dc3545" 
                                                                            : (appliedTheme.inputBorderColor || "#e5e7eb");
                                                                        e.target.style.boxShadow = `0 1px 3px rgba(0, 0, 0, ${appliedTheme.inputShadowIntensity || 0.05})`;
                                                                    }}
                                                                    pattern={(() => {
                                                                        const pattern = typeof step.validation?.pattern === "string"
                                                                            ? step.validation.pattern
                                                                            : step.validation?.pattern?.source;
                                                                        return pattern || undefined;
                                                                    })()}
                                                                    className={styles.singlePageInput}
                                                                    style={{
                                                                        borderRadius: appliedTheme.inputBorderRadius,
                                                                        borderColor: emailValidationErrors[step.label] 
                                                                            ? "#dc3545" 
                                                                            : (appliedTheme.inputBorderColor || "#d1d5db"),
                                                                        backgroundColor: appliedTheme.inputBackground,
                                                                        color: appliedTheme.inputTextColor,
                                                                        boxShadow: `0 1px 3px rgba(0, 0, 0, ${appliedTheme.inputShadowIntensity || 0.05})`,
                                                                        padding: "0.75rem 1rem",
                                                                        fontSize: "1rem",
                                                                    }}
                                                                    onFocus={(e) => {
                                                                        e.target.style.borderColor = appliedTheme.inputFocusBorderColor || "#348C9E";
                                                                        e.target.style.boxShadow = `0 0 0 4px rgba(52, 140, 158, 0.1), 0 4px 12px rgba(52, 140, 158, 0.15)`;
                                                                    }}
                                                                    onMouseEnter={(e) => {
                                                                        if (document.activeElement !== e.target) {
                                                                            e.target.style.borderColor = emailValidationErrors[step.label]
                                                                                ? "#dc3545"
                                                                                : (appliedTheme.inputHoverBorderColor || "#d1d5db");
                                                                        }
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        if (document.activeElement !== e.target) {
                                                                            e.target.style.borderColor = emailValidationErrors[step.label]
                                                                                ? "#dc3545"
                                                                                : (appliedTheme.inputBorderColor || "#e5e7eb");
                                                                        }
                                                                    }}
                                                                    placeholder={dynamicLabel}
                                                                />
                                                                {emailValidationErrors[step.label] && (
                                                                    <small style={{ 
                                                                        color: "#dc3545", 
                                                                        textAlign: "center", 
                                                                        display: "block", 
                                                                        marginTop: "0.25rem",
                                                                        fontSize: "0.875rem"
                                                                    }}>
                                                                        {emailValidationErrors[step.label]}
                                                                    </small>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <div className={step.type === "image" ? styles.singlePageImageWrapper : ""}>
                                                                <FormStepRenderer
                                                                    step={step}
                                                                    value={stepValue}
                                                                    onChange={(val) => handleStepChange(stepKey, val)}
                                                                    formData={formData}
                                                                    inventoryItems={inventoryItems}
                                                                    inventoryLoading={inventoryLoading}
                                                                    hideLabel={false}
                                                                    theme={appliedTheme}
                                                                    formStyle="singlePage"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    
                                    {/* Submit Button */}
                                    <div className={styles.singlePageSubmit}>
                                        <Button
                                            variant="success"
                                            disabled={submitting || submitDisabled}
                                            onClick={() => {
                                                if (formMode === "draft") {
                                                    toast.info("🚫 This form is in draft mode. Submissions are disabled.");
                                                    return;
                                                }
                                                onSubmit(formData);
                                            }}
                                            className={styles.singlePageSubmitButton}
                                            title={submitDisabled ? "Add files to all steps to continue" : undefined}
                                            style={{
                                                backgroundColor: appliedTheme.buttonColor,
                                                color: appliedTheme.buttonTextColor,
                                                borderRadius: appliedTheme.buttonBorderRadius || "8px",
                                                padding: "0.875rem 2rem",
                                                fontSize: "1rem",
                                                fontWeight: "600",
                                                border: "none",
                                                cursor: "pointer",
                                                transition: "all 0.2s ease",
                                                width: "100%",
                                                maxWidth: "300px",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = appliedTheme.buttonHoverColor || appliedTheme.buttonColor;
                                                e.target.style.transform = "translateY(-2px)";
                                                e.target.style.boxShadow = `0 4px 12px rgba(0, 0, 0, 0.15)`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = appliedTheme.buttonColor;
                                                e.target.style.transform = "translateY(0)";
                                                e.target.style.boxShadow = "none";
                                            }}
                                        >
                                            {submitButtonText}
                                        </Button>
                                        {submitDisabled && (
                                            <p className="text-muted small mt-2 mb-0" style={{ fontSize: "0.8rem" }}>
                                                Complete all steps above (add at least one file to each) to enable upload.
                                            </p>
                                        )}
                                    </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Typeform (Multi-step) View - existing code
    if (!currentStep) {
        return (
            <div className={styles.emptyState}>
                <p>No fields added yet. Use the form builder to add steps.</p>
            </div>
        );
    }

    const renderStepContent = (theme) => {
        switch (currentStep.type) {
            case "offer": {
                const selectedProduct = formData["Product Name"];
                const matchedDevice = inventoryItems.find(item =>
                    item.productName?.toLowerCase().trim() === selectedProduct?.toLowerCase().trim()
                );

                return (
                    <OfferDisplay
                        device={matchedDevice}
                        formData={formData}
                        items={inventoryItems}
                        onBack={goToPreviousStep}
                        onStartOver={restartForm}
                        goToNextStep={goToNextStep}
                        onChange={(key, value) => handleStepChange(key, value)}
                        step={currentStep}
                    />
                );
            }
            default:
                return (
                    <FormStepRenderer
                        step={currentStep}
                        value={formData[getStepKey(currentStep, currentStepIndex)]}
                        onChange={(val) => handleStepChange(getStepKey(currentStep, currentStepIndex), val)}
                        formData={formData}
                        inventoryItems={inventoryItems}
                        inventoryLoading={inventoryLoading}
                        hideLabel={true}
                        inputRef={inputRef}
                        theme={theme}
                    />
                );
        }
    };

    return (
        <div
            className={`${styles.previewContainer} ${formMode === "draft" ? styles.draftMode : styles.productionMode}`}
            style={{
                backgroundImage: theme?.backgroundImageUrl ? `url(${theme.backgroundImageUrl})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                fontFamily: appliedTheme.fontFamily
            }}
        >
            {formMode === "draft" && (
                <div
                    className={styles.draftBanner}
                    style={{
                        backgroundColor: appliedTheme.bannerBackground,
                    }}
                >
                    🚧 This form is in <strong>draft mode</strong>. Submissions are disabled.
                </div>
            )}

            <div className={styles.previewWrapper}>
                <div className={styles.stepContainer}>
                    <div
                        className={styles.backgroundOverlay}
                        style={{
                            backgroundColor: appliedTheme.backgroundColor,
                            opacity: appliedTheme.wrapperOpacity,
                            borderRadius: appliedTheme.formBorderRadius
                        }}
                    />
                    <div 
                        className={styles.contentInner}
                        style={{
                            backgroundColor: appliedTheme.cardBackgroundColor 
                                ? (() => {
                                    const rgb = hexToRgb(appliedTheme.cardBackgroundColor);
                                    return rgb 
                                        ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${appliedTheme.wrapperOpacity})`
                                        : `rgba(255, 255, 255, ${appliedTheme.wrapperOpacity})`;
                                })()
                                : `rgba(255, 255, 255, ${appliedTheme.wrapperOpacity})`,
                            borderRadius: appliedTheme.formBorderRadius || "24px",
                            padding: "3rem 2.5rem",
                        }}
                    >
                        {theme?.logoUrl && (
                            <div className={styles.logoContainer}>
                                <img src={theme.logoUrl} alt="Form Logo" className={styles.logoImage} />
                            </div>
                        )}
                        {showSteps && (
                            <div 
                                className={styles.stepIndicator}
                                style={stepIndicatorStyle === "progress" ? { marginBottom: "2rem" } : {}}
                            >
                                {stepIndicatorStyle === "progress" && (
                                    <>
                                        <span 
                                            className={styles.stepText}
                                            style={{
                                                color: appliedTheme.progressIndicatorInactiveTextColor || appliedTheme.instructionsColor || "#6c757d"
                                            }}
                                        >
                                            Step {currentStepIndex + 1} of {steps.length}
                                        </span>
                                        <div 
                                            className={styles.stepProgressBar}
                                            style={{
                                                backgroundColor: appliedTheme.progressBarBackground || "rgba(0, 0, 0, 0.06)"
                                            }}
                                        >
                                            <div 
                                                className={styles.stepProgressFill}
                                                style={{
                                                    width: `${((currentStepIndex + 1) / steps.length) * 100}%`,
                                                    backgroundColor: appliedTheme.progressBarColor || appliedTheme.progressIndicatorActiveColor || "#348C9E"
                                                }}
                                            />
                                        </div>
                                    </>
                                )}
                                
                                {stepIndicatorStyle === "checkbox" && (
                                    <div className={styles.checkboxIndicator}>
                                        <div className={styles.checkboxSteps}>
                                            {steps.map((step, index) => (
                                                <div 
                                                    key={index} 
                                                    className={`${styles.checkboxStep} ${index < currentStepIndex ? styles.completed : ''} ${index === currentStepIndex ? styles.current : ''}`}
                                                >
                                                    <div 
                                                        className={styles.checkboxCircle}
                                                        style={{
                                                            backgroundColor: index < currentStepIndex 
                                                                ? (appliedTheme.progressIndicatorActiveColor || "#348C9E")
                                                                : index === currentStepIndex
                                                                ? (appliedTheme.progressIndicatorActiveColor || "#348C9E")
                                                                : appliedTheme.progressIndicatorInactiveColor || "#e9ecef",
                                                            borderColor: index <= currentStepIndex 
                                                                ? (appliedTheme.progressIndicatorActiveColor || "#348C9E")
                                                                : appliedTheme.progressIndicatorConnectorColor || "#dee2e6"
                                                        }}
                                                    >
                                                        {index < currentStepIndex && (
                                                            <FaCheck className={styles.checkIcon} style={{ color: appliedTheme.progressIndicatorTextColor || "#ffffff" }} />
                                                        )}
                                                    </div>
                                                    {index < steps.length - 1 && (
                                                        <div 
                                                            className={styles.checkboxConnector}
                                                            style={{
                                                                backgroundColor: index < currentStepIndex 
                                                                    ? (appliedTheme.progressIndicatorActiveColor || "#348C9E")
                                                                    : appliedTheme.progressIndicatorConnectorColor || "#e9ecef"
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <div className={styles.checkboxProgressBar}>
                                            <div 
                                                className={styles.checkboxProgressFill}
                                                style={{
                                                    width: `${((currentStepIndex + 1) / steps.length) * 100}%`,
                                                    backgroundColor: appliedTheme.progressBarColor || appliedTheme.progressIndicatorActiveColor || "#348C9E"
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {stepIndicatorStyle === "checkboxOnly" && (
                                    <div className={styles.checkboxOnlyIndicator}>
                                        {steps.map((step, index) => (
                                            <div 
                                                key={index} 
                                                className={`${styles.checkboxStep} ${index < currentStepIndex ? styles.completed : ''} ${index === currentStepIndex ? styles.current : ''}`}
                                            >
                                                <div 
                                                    className={styles.checkboxCircle}
                                                    style={{
                                                        backgroundColor: index < currentStepIndex 
                                                            ? (appliedTheme.progressIndicatorActiveColor || "#348C9E")
                                                            : index === currentStepIndex
                                                            ? (appliedTheme.progressIndicatorActiveColor || "#348C9E")
                                                            : appliedTheme.progressIndicatorInactiveColor || "#e9ecef",
                                                        borderColor: index <= currentStepIndex 
                                                            ? (appliedTheme.progressIndicatorActiveColor || "#348C9E")
                                                            : appliedTheme.progressIndicatorConnectorColor || "#dee2e6"
                                                    }}
                                                >
                                                    {index < currentStepIndex && (
                                                        <FaCheck className={styles.checkIcon} style={{ color: appliedTheme.progressIndicatorTextColor || "#ffffff" }} />
                                                    )}
                                                </div>
                                                {index < steps.length - 1 && (
                                                    <div 
                                                        className={styles.checkboxConnector}
                                                        style={{
                                                            backgroundColor: index < currentStepIndex 
                                                                ? (appliedTheme.progressIndicatorActiveColor || "#348C9E")
                                                                : appliedTheme.progressIndicatorConnectorColor || "#e9ecef"
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                
                                {stepIndicatorStyle === "circles" && (
                                    <div className={styles.circlesIndicator}>
                                        {steps.map((step, index) => (
                                            <div 
                                                key={index} 
                                                className={styles.circleStepContainer}
                                            >
                                                <div 
                                                    className={`${styles.circleStep} ${index < currentStepIndex ? styles.completed : ''} ${index === currentStepIndex ? styles.current : ''}`}
                                                    style={{
                                                        backgroundColor: index < currentStepIndex 
                                                            ? (appliedTheme.progressIndicatorActiveColor || "#348C9E")
                                                            : index === currentStepIndex
                                                            ? (appliedTheme.progressIndicatorActiveColor || "#348C9E")
                                                            : "transparent",
                                                        borderColor: index <= currentStepIndex 
                                                            ? (appliedTheme.progressIndicatorActiveColor || "#348C9E")
                                                            : appliedTheme.progressIndicatorConnectorColor || "#dee2e6",
                                                        color: index <= currentStepIndex 
                                                            ? (appliedTheme.progressIndicatorTextColor || "#ffffff")
                                                            : (appliedTheme.progressIndicatorInactiveTextColor || "#6c757d")
                                                    }}
                                                >
                                                    {index < currentStepIndex ? (
                                                        <FaCheck className={styles.circleCheckIcon} style={{ color: appliedTheme.progressIndicatorTextColor || "#ffffff" }} />
                                                    ) : (
                                                        <span style={{ color: index <= currentStepIndex 
                                                            ? (appliedTheme.progressIndicatorTextColor || "#ffffff")
                                                            : (appliedTheme.progressIndicatorInactiveTextColor || "#6c757d")
                                                        }}>
                                                            {index + 1}
                                                        </span>
                                                    )}
                                                </div>
                                                {index < steps.length - 1 && (
                                                    <div 
                                                        className={styles.circleConnector}
                                                        style={{
                                                            backgroundColor: index < currentStepIndex 
                                                                ? (appliedTheme.progressIndicatorActiveColor || "#348C9E")
                                                                : appliedTheme.progressIndicatorConnectorColor || "#e9ecef"
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {isEditMode && editingHeader ? (
                            <input
                                ref={headerInputRef}
                                type="text"
                                value={headerValue}
                                onChange={(e) => setHeaderValue(e.target.value)}
                                onBlur={() => {
                                    if (onStepUpdate) {
                                        onStepUpdate(currentStepIndex, { ...currentStep, header: headerValue });
                                    }
                                    setEditingHeader(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.target.blur();
                                    } else if (e.key === "Escape") {
                                        setHeaderValue(currentStep.header || "");
                                        setEditingHeader(false);
                                    }
                                }}
                                className={styles.inlineEditInput}
                                style={{
                                    color: appliedTheme.headingColor,
                                    fontSize: appliedTheme.headerFontSize,
                                    fontWeight: appliedTheme.headerFontWeight,
                                    backgroundColor: "transparent",
                                    border: "2px dashed " + appliedTheme.buttonColor,
                                    borderRadius: "8px",
                                    padding: "0.5rem",
                                    width: "100%",
                                    outline: "none",
                                }}
                                autoFocus
                            />
                        ) : (
                            (currentStep.header || isEditMode) && (
                                <h2
                                    className={styles.stepHeader}
                                    style={{
                                        color: appliedTheme.headingColor,
                                        fontSize: appliedTheme.headerFontSize,
                                        fontWeight: appliedTheme.headerFontWeight,
                                        cursor: isEditMode ? "pointer" : "default",
                                        position: "relative",
                                    }}
                                    onClick={() => {
                                        if (isEditMode) {
                                            setEditingHeader(true);
                                        }
                                    }}
                                    title={isEditMode ? "Click to edit" : ""}
                            >
                                {currentStep.type === "offer" && formData["Product Name"] 
                                        ? (currentStep.header || "").replace('selected item', formData["Product Name"])
                                        : (currentStep.header || "Click to add header")
                                }
                            </h2>
                            )
                        )}
                        {isEditMode && editingInstructions ? (
                            <textarea
                                ref={instructionsInputRef}
                                value={instructionsValue}
                                onChange={(e) => setInstructionsValue(e.target.value)}
                                onBlur={() => {
                                    if (onStepUpdate) {
                                        onStepUpdate(currentStepIndex, { ...currentStep, instructions: instructionsValue });
                                    }
                                    setEditingInstructions(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Escape") {
                                        setInstructionsValue(currentStep.instructions || "");
                                        setEditingInstructions(false);
                                    }
                                }}
                                className={styles.inlineEditInput}
                                style={{
                                    color: appliedTheme.instructionsColor,
                                    fontSize: appliedTheme.instructionsFontSize,
                                    backgroundColor: "transparent",
                                    border: "2px dashed " + appliedTheme.buttonColor,
                                    borderRadius: "8px",
                                    padding: "0.5rem",
                                    width: "100%",
                                    outline: "none",
                                    minHeight: "3rem",
                                    resize: "vertical",
                                }}
                                autoFocus
                            />
                        ) : (
                            (currentStep.instructions || isEditMode) && (
                                <p
                                    className={styles.stepInstructions}
                                    style={{
                                        color: appliedTheme.instructionsColor,
                                        fontSize: appliedTheme.instructionsFontSize,
                                        cursor: isEditMode ? "pointer" : "default",
                                        minHeight: isEditMode ? "1.5rem" : "auto",
                                    }}
                                    onClick={() => {
                                        if (isEditMode) {
                                            setEditingInstructions(true);
                                        }
                                    }}
                                    title={isEditMode ? "Click to edit" : ""}
                            >
                                {currentStep.type === "offer" && formData["Product Name"]
                                        ? (currentStep.instructions || "").replace('selected item', formData["Product Name"])
                                        : (currentStep.instructions || "Click to add instructions")
                                }
                            </p>
                            )
                        )}

                        <div className={styles.stepContent}>
                            {renderStepContent(appliedTheme)}
                        </div>

                        {currentStep.type !== "offer" && (
                            <div className={styles.navigation}>
                                {currentStepIndex > 0 && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={goToPreviousStep}
                                        style={{
                                            backgroundColor: "#ffffff",
                                            color: appliedTheme.buttonColor,
                                            borderColor: appliedTheme.buttonColor,
                                            borderWidth: "2px",
                                            borderRadius: appliedTheme.buttonBorderRadius,
                                            boxShadow: `0 4px 12px rgba(0, 0, 0, ${appliedTheme.buttonShadowIntensity})`,
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = "#f0f9fa";
                                            e.target.style.borderColor = appliedTheme.buttonHoverColor || appliedTheme.buttonColor;
                                            e.target.style.color = appliedTheme.buttonHoverColor || appliedTheme.buttonColor;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = "#ffffff";
                                            e.target.style.borderColor = appliedTheme.buttonColor;
                                            e.target.style.color = appliedTheme.buttonColor;
                                        }}
                                    >
                                        ← Back
                                    </Button>
                                )}

                                {inputComplete && !isLastStep && (
                                    <Button
                                        variant="primary"
                                        onClick={goToNextStep}
                                        style={{
                                            backgroundColor: appliedTheme.buttonColor,
                                            color: appliedTheme.buttonTextColor,
                                            borderRadius: appliedTheme.buttonBorderRadius,
                                            boxShadow: `0 4px 12px rgba(0, 0, 0, ${appliedTheme.buttonShadowIntensity})`,
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.backgroundColor = appliedTheme.buttonHoverColor || appliedTheme.buttonColor;
                                            e.target.style.boxShadow = `0 8px 24px rgba(0, 0, 0, ${parseFloat(appliedTheme.buttonShadowIntensity) * 1.5})`;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.backgroundColor = appliedTheme.buttonColor;
                                            e.target.style.boxShadow = `0 4px 12px rgba(0, 0, 0, ${appliedTheme.buttonShadowIntensity})`;
                                        }}
                                    >
                                        Next →
                                    </Button>
                                )}

                                {isLastStep && (
                                    <div className={styles.endMessage}>
                                        <Button
                                            variant="success"
                                            disabled={submitting || submitDisabled}
                                            onClick={() => {
                                                if (formMode === "draft") {
                                                    toast.info("🚫 This form is in draft mode. Submissions are disabled.");
                                                    return;
                                                }
                                                onSubmit(formData);
                                            }}
                                            style={{
                                                backgroundColor: appliedTheme.buttonColor,
                                                color: appliedTheme.buttonTextColor,
                                                borderRadius: appliedTheme.buttonBorderRadius,
                                                boxShadow: `0 6px 20px rgba(0, 0, 0, ${parseFloat(appliedTheme.buttonShadowIntensity) * 1.5})`,
                                            }}
                                            title={submitDisabled ? "Add files to all steps to continue" : undefined}
                                            onMouseEnter={(e) => {
                                                e.target.style.backgroundColor = appliedTheme.buttonHoverColor || appliedTheme.buttonColor;
                                                e.target.style.boxShadow = `0 12px 32px rgba(0, 0, 0, ${parseFloat(appliedTheme.buttonShadowIntensity) * 2})`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.backgroundColor = appliedTheme.buttonColor;
                                                e.target.style.boxShadow = `0 6px 20px rgba(0, 0, 0, ${parseFloat(appliedTheme.buttonShadowIntensity) * 1.5})`;
                                            }}
                                        >
                                        {submitButtonText}
                                        </Button>
                                        {submitDisabled && (
                                            <p className="text-muted small mt-2 mb-0" style={{ fontSize: "0.8rem" }}>
                                                Complete all steps (add at least one file to each) to enable upload.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormPreview;
